// Compiled `calling → order` table for render-time seat sorting.
//
// Per `extension/docs/sync-design.md` "Grant-derived seat type (Stage 1
// + Stage 2)" part (a): the roster / All Seats sort no longer reads the
// denormalised `seat.sort_order`. Instead it computes order at render
// time from the seat's callings against this canonical, churchwide
// ordering table.
//
// The table is a fixed 92-entry hierarchy (stake callings 1–42, unit
// callings 43–92). The array index is the priority — lower index sorts
// first. The ordering is global: the calling hierarchy is churchwide,
// so there is no per-stake customisation (see Stage 1 open question #3).
//
// The unit band covers both kinds of unit: each branch calling sits
// immediately after its ward counterpart (Branch President after Bishop,
// Branch Clerk after Ward Clerk, …). All Seats spans units, so a mixed
// list has to rank a branch calling against a ward one; interleaving
// keeps peers together instead of stranding a Branch President below
// every ward calling in the band. Within one unit only one of each pair
// can occur, so the tie-break is unobservable there.
//
// Matching is exact, trimmed, case-insensitive — NO wildcards. A
// calling that isn't in the table resolves to `null` ("unknown"), which
// the sort comparator banishes to the bottom of its type band.
//
// THE TABLE IS EXPORTED AS ITS TWO BANDS, NOT AS ONE ARRAY PLUS A
// BOUNDARY (T-99).
//
// `callingSortOrder` is a name→order lookup, and a lookup cannot be
// enumerated — which is why the web request typeahead carried a
// hand-maintained copy of all 92 names until T-99, and why T-96's seven
// branch callings reached this table and silently not that one.
// `STAKE_CALLING_ORDER` and `UNIT_CALLING_ORDER` are what a consumer
// needing a *list* projects from.
//
// They are two exports rather than one because the stake/unit boundary
// is a property of this table, not of any consumer. Exporting only the
// whole array would make every list-consumer slice at
// `indexOf('Bishop')` — the boundary restated as a magic string, in
// code that has no way to know it moved. `CALLING_ORDER` stays the
// concatenation, so every index this module hands out is unchanged;
// changing them would re-break the stored `access.sort_order` the way
// T-96 did.
//
// A new calling goes in one of the two bands. Nothing may be appended
// to `CALLING_ORDER` outside them — a third band would be invisible to
// every consumer enumerating the two, which is the exact failure T-99
// removed. `callingSortOrder.test.ts` pins that.
/** Stake-band callings (1–42), in priority order. */
export const STAKE_CALLING_ORDER = [
    'Stake President',
    'Stake Presidency First Counselor',
    'Stake Presidency Second Counselor',
    'Stake Clerk',
    'Stake Executive Secretary',
    'Stake Assistant Executive Secretary',
    'Stake Assistant Clerk',
    'Stake Assistant Clerk--Membership',
    'Stake Assistant Clerk--Finance',
    'Stake High Councilor',
    'Stake Relief Society President',
    'Stake Relief Society First Counselor',
    'Stake Relief Society Second Counselor',
    'Stake Relief Society Secretary',
    'Stake Young Men President',
    'Stake Young Men First Counselor',
    'Stake Young Men Second Counselor',
    'Stake Young Men Secretary',
    'Stake Young Women President',
    'Stake Young Women First Counselor',
    'Stake Young Women Second Counselor',
    'Stake Young Women Secretary',
    'Stake Sunday School President',
    'Stake Sunday School First Counselor',
    'Stake Sunday School Second Counselor',
    'Stake Sunday School Secretary',
    'Stake Primary President',
    'Stake Primary First Counselor',
    'Stake Primary Second Counselor',
    'Stake Primary Secretary',
    'Stake Building Representative',
    'Stake Building Specialist',
    'Stake Technology Specialist',
    'Stake Single Adult Adviser',
    'Stake Single Adult Representative',
    'Stake Young Single Adult Advisor',
    'Stake Young Single Adult Representative',
    'Stake Music Chairman',
    'Audit Committee Chairman',
    'Audit Committee Member',
    'Auditor',
    'Patriarch',
];
/**
 * Unit-band callings (43–92), in priority order — both kinds of unit,
 * each branch calling immediately after its ward counterpart.
 */
export const UNIT_CALLING_ORDER = [
    'Bishop',
    'Branch President',
    'Bishopric First Counselor',
    'Branch Presidency First Counselor',
    'Bishopric Second Counselor',
    'Branch Presidency Second Counselor',
    // No Branch Executive Secretary — branches have no counterpart to
    // Ward Executive Secretary.
    'Ward Executive Secretary',
    'Ward Assistant Executive Secretary',
    'Ward Clerk',
    'Branch Clerk',
    'Ward Assistant Clerk',
    'Branch Assistant Clerk',
    'Ward Assistant Clerk--Membership',
    'Branch Assistant Clerk--Membership',
    'Ward Assistant Clerk--Finance',
    'Branch Assistant Clerk--Finance',
    // The four families below are shared: the same entries rank a branch's
    // people as a ward's.
    'Elders Quorum President',
    'Elders Quorum First Counselor',
    'Elders Quorum Second Counselor',
    'Elders Quorum Secretary',
    'Elders Quorum Assistant Secretary',
    'Relief Society President',
    'Relief Society First Counselor',
    'Relief Society Second Counselor',
    'Relief Society Secretary',
    'Aaronic Priesthood Advisors',
    'Aaronic Priesthood Specialist',
    'Young Women President',
    'Young Women First Counselor',
    'Young Women Second Counselor',
    'Young Women Secretary',
    'Young Women Specialist',
    'Young Women Class Adviser',
    'Sunday School President',
    'Sunday School First Counselor',
    'Sunday School Second Counselor',
    'Sunday School Secretary',
    'Primary President',
    'Primary First Counselor',
    'Primary Second Counselor',
    'Primary Secretary',
    'Valiant Activities Leader',
    'Ward Mission Leader',
    'Assistant Ward Mission Leader',
    'Ward Temple and Family History Leader',
    'Young Single Adult Adviser',
    'Building Representative',
    'History Specialist',
    'Email Communication Specialist',
    'Technology Specialist',
];
/**
 * Canonical calling order — the two bands, concatenated. Index =
 * priority (lower sorts first). The source of truth for the lookup map;
 * the bands are the source of truth for it.
 */
export const CALLING_ORDER = [...STAKE_CALLING_ORDER, ...UNIT_CALLING_ORDER];
/** Normalisation key: trimmed + lower-cased. Matching is exact on this key. */
function normalize(calling) {
    return calling.trim().toLowerCase();
}
/** `normalized calling → priority index`. Built once from `CALLING_ORDER`. */
const ORDER_BY_CALLING = new Map(CALLING_ORDER.map((name, index) => [normalize(name), index]));
/**
 * Resolve a single calling's sort priority. Trimmed + case-insensitive
 * exact match against the canonical table. Returns the priority index
 * (lower sorts first) or `null` when the calling isn't in the table.
 */
export function callingSortOrder(calling) {
    const order = ORDER_BY_CALLING.get(normalize(calling));
    return order === undefined ? null : order;
}
/**
 * Resolve a seat's sort priority from its callings: the MIN order across
 * every calling that matches the table. Returns `null` when the seat has
 * no callings, or none of its callings match (the comparator treats
 * `null` as "unknown" → bottom of the type band).
 */
export function seatCallingOrder(callings) {
    let min = null;
    for (const calling of callings) {
        const order = callingSortOrder(calling);
        if (order === null)
            continue;
        if (min === null || order < min)
            min = order;
    }
    return min;
}
//# sourceMappingURL=callingSortOrder.js.map